import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Link, makeStyles, TextField, Typography } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Account, accountsForSelectedCompany, buildAccounts, GROUPS, statusLabels } from "../../accounts";
import { useSysadminData } from "../../SysadminData";
import { initialForm, runSteps, saveSteps } from "./saveAccount";

const useStyles = makeStyles(theme => ({
    option: {
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
    },
    note: {
        marginTop: theme.spacing(1),
    },
}));

interface Props {
    eaterGroupId: string;
    onClose: () => void;
    onInvite: () => void;
}

/**
 * Adds several users to an eater group at once. Each user is placed in the eater group and made an eater,
 * so they can book right away.
 */
export const AddMembersDialog: React.FC<Props> = ({ eaterGroupId, onClose, onInvite }) => {
    const classes = useStyles();
    const data = useSysadminData();
    const [selected, setSelected] = useState<Account[]>([]);
    const [saving, setSaving] = useState<string>();
    const group = data.eaterGroupById(eaterGroupId);
    const officeId = group?.officeId ?? "";
    const groupName = group?.eaterGroupName || "eater group";

    const options = useMemo(
        () => accountsForSelectedCompany(data, buildAccounts(data))
            .filter(a => a.serviceUser && a.user?.eaterGroupId !== eaterGroupId)
            // Users without an eater group first, as they are most likely the ones to add
            .sort((a, b) => Number(!!a.user?.eaterGroupId) - Number(!!b.user?.eaterGroupId) || a.email.localeCompare(b.email)),
        [data, eaterGroupId]
    );
    const describe = (a: Account) => {
        const current = a.user?.eaterGroupId ? data.eaterGroupById(a.user.eaterGroupId)?.eaterGroupName ?? "another eater group" : undefined;
        return [current ? `In ${current}` : "No eater group", a.status === "unverified" ? statusLabels.unverified : undefined].filter(Boolean).join(" · ");
    };

    const moved = selected.filter(a => a.user?.eaterGroupId);
    const unverified = selected.filter(a => a.status === "unverified");

    async function add() {
        const failed: string[] = [];
        for (const [index, account] of selected.entries()) {
            setSaving(`Adding ${index + 1} of ${selected.length}…`);
            const original = initialForm(account, data);
            const groups = original.groups.includes(GROUPS.eater) ? original.groups : [...original.groups, GROUPS.eater];
            const form = { ...original, companyId: data.companyIdOfOffice(officeId) ?? "", officeId, eaterGroupId, groups };
            const result = await runSteps(saveSteps(account, original, form, () => ""));
            if (result.failed) failed.push(`${account.email} (${result.failed.label}: ${result.failed.message})`);
        }
        await data.reload();
        const added = selected.length - failed.length;
        if (added > 0) toast.success(`Added ${added} ${added === 1 ? "user" : "users"} to ${groupName}`);
        if (failed.length) {
            toast.error(`Could not add ${failed.join(", ")}`, { duration: 10000 });
            setSelected(selected.filter(a => failed.some(f => f.startsWith(a.email + " "))));
            setSaving(undefined);
        } else {
            onClose();
        }
    }

    return (
        <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>Add users to {groupName}</DialogTitle>
            <DialogContent>
                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={options}
                    value={selected}
                    onChange={(_, value) => setSelected(value)}
                    getOptionLabel={a => a.email}
                    getOptionSelected={(a, b) => a.email === b.email}
                    groupBy={a => (a.user?.eaterGroupId ? "In another eater group" : "Without eater group")}
                    renderOption={(a, { selected: checked }) => (
                        <>
                            <Checkbox size="small" checked={checked} style={{ marginRight: 8 }} />
                            <div className={classes.option}>
                                <span>{a.email}</span>
                                <Typography variant="caption" color="textSecondary">{describe(a)}</Typography>
                            </div>
                        </>
                    )}
                    renderInput={params => <TextField {...params} autoFocus variant="outlined" label="Users" placeholder={selected.length ? "" : "Search by email"} />}
                    noOptionsText="No users found"
                    disabled={!!saving}
                />
                <Typography variant="body2" color="textSecondary" className={classes.note}>
                    The users are placed in {groupName} and become eaters, so they can book.
                </Typography>
                {moved.length > 0 && (
                    <Typography variant="body2" className={classes.note} style={{ color: "#8a5300" }}>
                        {moved.length === 1 ? "1 user is" : `${moved.length} users are`} moved from their current eater group: {moved.map(a => a.email).join(", ")}
                    </Typography>
                )}
                {unverified.length > 0 && (
                    <Typography variant="body2" className={classes.note} style={{ color: "#8a5300" }}>
                        {unverified.length === 1 ? "1 user has" : `${unverified.length} users have`} not verified their email yet and cannot log in before they do.
                    </Typography>
                )}
                <Typography variant="body2" style={{ marginTop: 16 }}>
                    New to PlanEat? <Link component="button" variant="body2" onClick={onInvite} disabled={!!saving}>Invite a new user</Link>
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={!!saving}>Cancel</Button>
                <Button variant="contained" color="primary" disabled={!!saving || selected.length === 0} onClick={add}>
                    {saving ?? (selected.length > 1 ? `Add ${selected.length} users` : "Add user")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
